import { useCallback } from 'react';
import { motion } from 'framer-motion';
import { Type, AlignLeft, List, Hash, Calendar, Table2, ToggleLeft } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import TableSelectField from './TableSelectField';

const fieldTypeIcons = {
  text: Type,
  textarea: AlignLeft,
  select: List,
  'table-select': Table2,
  number: Hash,
  date: Calendar,
  boolean: ToggleLeft,
};

export default function StepForm({ fields, values, onChange, disabled }) {
  const handleChange = useCallback(
    (name, value) => {
      onChange((prev) => ({ ...prev, [name]: value }));
    },
    [onChange]
  );

  return (
    <div className="space-y-4">
      {fields.map((field, index) => {
        const IconComp = fieldTypeIcons[field.type] || Type;

        return (
          <motion.div
            key={field.name}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25, delay: index * 0.05 }}
            className="space-y-1.5"
          >
            {field.type === 'boolean' ? (
              <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2.5">
                <div className="flex items-center gap-2">
                  <IconComp className="size-3.5 text-muted-foreground" />
                  <Label className="cursor-pointer mb-0">
                    {field.label}
                    {field.required && <span className="text-destructive ml-1">*</span>}
                  </Label>
                </div>
                <Switch
                  checked={!!values[field.name]}
                  onCheckedChange={(val) => handleChange(field.name, val)}
                  disabled={disabled}
                />
              </div>
            ) : (
              <>
                <Label className="flex items-center gap-1.5">
                  <IconComp className="size-3.5 text-muted-foreground" />
                  {field.label}
                  {field.required && <span className="text-destructive ml-1">*</span>}
                </Label>

                {field.description && (
                  <p className="text-xs text-muted-foreground">{field.description}</p>
                )}

                {field.type === 'table-select' ? (
                  <TableSelectField
                    field={field}
                    value={values[field.name] || ''}
                    onChange={(val) => handleChange(field.name, val)}
                    disabled={disabled}
                  />
                ) : field.type === 'textarea' ? (
                  <Textarea
                    value={values[field.name] || ''}
                    onChange={(e) => handleChange(field.name, e.target.value)}
                    disabled={disabled}
                    required={field.required}
                    rows={3}
                    placeholder={field.label}
                  />
                ) : field.type === 'select' && field.options ? (
                  <Select
                    value={values[field.name] || ''}
                    onValueChange={(val) => handleChange(field.name, val)}
                    disabled={disabled}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder={`-- ${field.label} --`} />
                    </SelectTrigger>
                    <SelectContent>
                      {field.options.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : field.type === 'number' ? (
                  <Input
                    type="number"
                    value={values[field.name] || ''}
                    onChange={(e) => handleChange(field.name, e.target.value)}
                    disabled={disabled}
                    required={field.required}
                    placeholder={field.label}
                  />
                ) : field.type === 'date' ? (
                  <Input
                    type="date"
                    value={values[field.name] || ''}
                    onChange={(e) => handleChange(field.name, e.target.value)}
                    disabled={disabled}
                    required={field.required}
                  />
                ) : (
                  <Input
                    type="text"
                    value={values[field.name] || ''}
                    onChange={(e) => handleChange(field.name, e.target.value)}
                    disabled={disabled}
                    required={field.required}
                    placeholder={field.label}
                  />
                )}
              </>
            )}
          </motion.div>
        );
      })}
    </div>
  );
}
